import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Check, Sparkles } from 'lucide-react';

// Daily System Task with First-Time User Onboarding
// - On first launch: Shows "What were you about to do?"
// - After first task added: Shows helper about "Find my thread"
// - After onboarding complete: Shows normal "Anything on your Calendar today?"
// - Only one prompt card at a time

const SYSTEM_TASK_KEY = 'wwi_daily_calendar_task';
const ONBOARDING_KEY = 'wwi_onboarding_complete';
const FIRST_TASK_KEY = 'wwi_first_task_added';

const getTodayKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
};

export const useDailySystemTask = (enabled = true) => {
  const [showTask, setShowTask] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [showThreadHelper, setShowThreadHelper] = useState(false);

  // Check state on mount and periodically
  useEffect(() => {
    if (!enabled) {
      setShowTask(false);
      return;
    }

    const checkState = () => {
      // Check onboarding status
      const onboardingComplete = localStorage.getItem(ONBOARDING_KEY) === 'true';
      const firstTaskAdded = localStorage.getItem(FIRST_TASK_KEY) === 'true';

      if (!onboardingComplete) {
        // First-time user flow
        setIsOnboarding(true);
        setShowTask(true);
        setShowThreadHelper(firstTaskAdded);
        return;
      }

      // Normal daily task flow (onboarding complete)
      setIsOnboarding(false);
      setShowThreadHelper(false);
      
      const todayKey = getTodayKey();
      const stored = localStorage.getItem(SYSTEM_TASK_KEY);
      
      if (stored) {
        try {
          const data = JSON.parse(stored);
          if (data.date !== todayKey) {
            setShowTask(true);
            setIsCompleted(false);
          } else {
            setShowTask(!data.completed);
            setIsCompleted(data.completed);
          }
        } catch {
          setShowTask(true);
          setIsCompleted(false);
        }
      } else {
        setShowTask(true);
        setIsCompleted(false);
      }
    };

    checkState();
    // Poll for changes (handles cross-component updates)
    const interval = setInterval(checkState, 300);
    return () => clearInterval(interval);
  }, [enabled]);

  const completeTask = useCallback(() => {
    const onboardingComplete = localStorage.getItem(ONBOARDING_KEY) === 'true';
    
    if (!onboardingComplete && showThreadHelper) {
      // Complete onboarding
      localStorage.setItem(ONBOARDING_KEY, 'true');
      setIsOnboarding(false);
      setShowThreadHelper(false);
      // Now show the normal daily task
      setShowTask(true);
      setIsCompleted(false);
      return;
    }
    
    if (!onboardingComplete) {
      // Still in onboarding, don't complete yet
      return;
    }

    // Normal completion
    const todayKey = getTodayKey();
    localStorage.setItem(SYSTEM_TASK_KEY, JSON.stringify({
      date: todayKey,
      completed: true
    }));
    setIsCompleted(true);
    setTimeout(() => {
      setShowTask(false);
    }, 300);
  }, [showThreadHelper]);

  return { showTask, isCompleted, completeTask, isOnboarding, showThreadHelper };
};

// Utility to mark first task as added from AppPage
export const triggerFirstTaskAdded = () => {
  const onboardingComplete = localStorage.getItem(ONBOARDING_KEY) === 'true';
  if (!onboardingComplete) {
    localStorage.setItem(FIRST_TASK_KEY, 'true');
  }
};

export const DailySystemTask = ({ enabled = true }) => {
  const { showTask, isCompleted, completeTask, isOnboarding, showThreadHelper } = useDailySystemTask(enabled);

  if (!enabled || !showTask) return null;

  // Onboarding state: "What were you about to do?"
  if (isOnboarding && !showThreadHelper) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
          className="mb-3"
          data-testid="onboarding-prompt"
        >
          <div className="bg-gradient-to-br from-[#FFF7ED] to-[#FFFBF5] border border-[#FFEDD5] rounded-xl px-3 py-2.5 shadow-sm">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-light text-[#F97316] uppercase tracking-wider opacity-90">
                Welcome
              </span>
            </div>
            
            <div className="flex items-center gap-3">
              <Sparkles size={16} className="text-[#F97316] flex-shrink-0" />
              <p className="text-sm text-[#52525B] font-light">
                What were you <span className="font-medium text-[#F97316]">about to do</span>?
              </p>
            </div>
            
            <p className="text-[11px] text-[#A1A1AA] mt-2 ml-7">
              Add a task below to get started.
            </p>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Thread helper state: After first task added
  if (isOnboarding && showThreadHelper) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
          className="mb-3"
          data-testid="thread-helper-prompt"
        >
          <div className="bg-gradient-to-br from-[#F0FDFA] to-[#F5FFFE] border border-[#CCFBF1] rounded-xl px-3 py-2.5 shadow-sm">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-light text-[#0D9488] uppercase tracking-wider opacity-90">
                Tip
              </span>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={completeTask}
                className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-[#0D9488] hover:bg-[#0D9488] hover:border-[#0D9488] transition-colors group flex items-center justify-center"
                data-testid="thread-helper-complete"
              >
                <Check 
                  size={12} 
                  className="text-transparent group-hover:text-white transition-colors" 
                />
              </button>
              
              <p className="text-sm text-[#52525B] font-light">
                If you drift, tap <span className="font-medium text-[#0D9488]">'Find my thread.'</span>
              </p>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Normal daily task: "Anything on your Calendar today?"
  return (
    <AnimatePresence>
      {showTask && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: isCompleted ? 0 : 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
          className="mb-3"
          data-testid="daily-system-task"
        >
          <div className="bg-[#F0FAFA] border border-[#E0F2F2] rounded-xl px-3 py-2 shadow-sm scale-95 origin-top" style={{ transform: 'scaleX(1) scaleY(0.95)' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-light text-[#78909C] uppercase tracking-wider opacity-80">
                Daily
              </span>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={completeTask}
                className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-[#3DC4D0] hover:bg-[#3DC4D0] hover:border-[#3DC4D0] transition-colors group flex items-center justify-center"
                data-testid="daily-task-complete"
              >
                <Check 
                  size={12} 
                  className="text-transparent group-hover:text-white transition-colors" 
                />
              </button>
              
              <Calendar size={16} className="text-[#78909C] flex-shrink-0" />
              
              <p className="text-sm text-[#52525B] font-light">
                Anything on your <span className="font-medium">Calendar</span> today?
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DailySystemTask;
